import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBlocker, useLocation, useNavigate, useParams } from 'react-router-dom';

import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { message, notification, Select, Spin } from 'antd';
import dayjs from 'dayjs';

import { getDeploymentStatus, updateDeployment } from '@/shared/api/deploy';
import { DEPLOYMENT_STATUS, type DeploymentStatus } from '@/shared/api/deploy/types';
import {
  cancelExecution,
  executeRun,
  type ExecutionStatus,
  getExecution,
  getLatestExecution,
} from '@/shared/api/execution';
import { LIBRARY_IMPORT_MAP } from '@/shared/constants/libraryImportMap';
import { NOTIFICATION_CONFIG } from '@/shared/lib/constants';
import { formatAxiosErrorForLog } from '@/shared/lib/errors/formatAxiosErrorForLog';
import { mapExecutionStatusToRunStatus } from '@/shared/lib/mapExecutionStatusToRunStatus';
import { experimentKeys } from '@/shared/lib/query/queryKeys';
import type { Library, WorkbenchAssets, WorkbenchState } from '@/shared/types';
import { Button } from '@/shared/ui/Button';
import { ConfirmModal } from '@/shared/ui/ConfirmModal';
import { ContentHeader } from '@/shared/ui/ContentHeader';
import { Icon } from '@/shared/ui/Icon';
import { isValidRunStatus, type RunStatus, RunStatusTag } from '@/shared/ui/RunStatusTag';
import { LoadingState } from '@/shared/ui/states/LoadingState';
import { ToggleSwitch } from '@/shared/ui/ToggleSwitch';
import { Tooltip } from '@/shared/ui/Tooltip';

import { fetchAssetDetail } from '@/entities/asset-search/api/assetDetailApi';
import { uploadModule, useModules } from '@/entities/langserver';
import {
  type ExecutionResultModalMetric,
  getFallbackExecutionResultModalMetrics,
  mapRunMetricsResponseToExecutionModalMetrics,
  runManagementApi,
} from '@/entities/run';

import {
  type AutoImport,
  type BuildResponse,
  fetchBuild,
  fetchParse,
  type TemplateSection,
} from '@/features/code-analysis';
import { shouldBootstrapDeploymentFromRunApi, useDeploymentManager } from '@/features/deployment';
import { useExecutionLogs } from '@/features/execution';
import { prefixWorkbenchLogBlock } from '@/features/execution/lib/prefixWorkbenchLogLine';
import { splitExperimentTemplateCode } from '@/features/experiment-workbench/lib/experimentCodeTemplate';

import {
  COMPUTING_RESOURCE_OPTIONS,
  COMPUTING_RESOURCE_TOOLTIP,
} from '@/widgets/experiments/ResourceTierSelector/resourceTierConfig';
import { DeploymentModal } from '@/widgets/workbench/DeploymentModal';
import { ExecutionResultModal } from '@/widgets/workbench/ExecutionResultModal';
import { Workbench } from '@/widgets/workbench/Workbench';

import styles from './ExperimentWorkbenchPage.module.css';
import { bumpRunIterationDisplayName } from './lib/bumpRunDisplayName';
import { getInstalledLibrariesFromExecution } from './lib/executionInstalledLibraries';

const EXECUTION_FAILED_FALLBACK_CAUSE = '서버에서 전달된 상세 원인이 없습니다. 로그 패널에서 확인해 주세요.';

const EXECUTION_CLICK_DEBOUNCE_MS = 600;

const EXECUTION_CANCELED_LOG_MARKER = '실행이 취소되었습니다';

type WorkbenchLocationData = {
  project: {
    id: number;
    name: string;
  };
  experiment: {
    id: number;
    name: string;
  };
  run: {
    name: string;
    description?: string;
    status?: string;
  };
  selectedAssets?: {
    datasetId?: number;
    featureSetId?: number;
    parameterSetId?: number;
  };
  assetDetails?: WorkbenchAssets;
  computingResource?: string;
  editorSnapshot?: WorkbenchState;
  experimentTags?: string[];
};

type LocationState = {
  workbenchData?: WorkbenchLocationData;
  showNewRunNotice?: string;
  isNewExecution?: boolean;
};

type BuildValidationIssue = {
  type?: string;
  message?: string;
  module?: string | null;
  source_file?: string;
  line?: number;
};

type BuildValidationError = {
  error: 'build_validation_failed';
  issues: BuildValidationIssue[];
};

/**
 * 사이드바에서 dataset/feature/parameter를 비우면 `undefined`로 들어오는데,
 * `undefined ?? 이전런값`이 되면 이전 런 자산이 되살아난다.
 * `Object.hasOwn`으로 사용자가 해당 키를 한 번이라도 내려보냈는지 구분한다.
 */
function mergeWorkbenchAssetsForNewRun(
  current: WorkbenchAssets,
  previousDetails: WorkbenchAssets | undefined
): WorkbenchAssets {
  return {
    dataset: Object.hasOwn(current, 'dataset') ? current.dataset : previousDetails?.dataset,
    featureSet: Object.hasOwn(current, 'featureSet') ? current.featureSet : previousDetails?.featureSet,
    parameterSet: Object.hasOwn(current, 'parameterSet') ? current.parameterSet : previousDetails?.parameterSet,
  };
}

function pickSelectedAssetId(
  assetKey: keyof WorkbenchAssets,
  idKey: 'datasetId' | 'featureSetId' | 'parameterSetId',
  current: WorkbenchAssets,
  previousSelected: WorkbenchLocationData['selectedAssets']
): number | undefined {
  if (!Object.hasOwn(current, assetKey)) {
    return previousSelected?.[idKey];
  }
  const asset = current[assetKey];
  return asset?.id;
}

function isWorkbenchRunCompletedForDeploy(status: string | undefined): boolean {
  if (!status || !isValidRunStatus(status)) return false;
  return shouldBootstrapDeploymentFromRunApi(status);
}

export const ExperimentWorkbenchPage: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const clientId = runId || 'demo-user';
  const {
    data: modulesData,
    isPending: isModulesLoading,
    isError: isModulesError,
    refetch: modulesRefetch,
  } = useModules('run', clientId);

  const workbenchData = (location.state as LocationState)?.workbenchData;
  const workbenchRouteReady = Boolean(workbenchData);

  const {
    data: runDetailData,
    isLoading: isRunDetailLoading,
    refetch: refetchRunDetail,
  } = useQuery({
    queryKey: experimentKeys.assetDetail(runId!, 'run', parseInt(runId!, 10)),
    queryFn: () => fetchAssetDetail({ assetId: runId!, type: 'run' }),
    enabled: !!runId,
  });

  const [isExecuting, setIsExecuting] = useState(false);
  const [isWorkbenchInitialLoading, setIsWorkbenchInitialLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(() => isWorkbenchRunCompletedForDeploy(workbenchData?.run?.status));
  const [isPreparingResult, setIsPreparingResult] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [isDeploymentModalOpen, setIsDeploymentModalOpen] = useState(false);
  const [pageLeaveConfirmOpen, setPageLeaveConfirmOpen] = useState(false);
  const [newRunNoticeRunName, setNewRunNoticeRunName] = useState<string | null>(null);
  const [deploymentMode, setDeploymentMode] = useState<'deploy' | 'start' | 'stop'>('deploy');
  const [stopAcknowledgementOnly, setStopAcknowledgementOnly] = useState(false);
  const [startAcknowledgementOnly, setStartAcknowledgementOnly] = useState(false);
  const [isServingToggleLoading, setIsServingToggleLoading] = useState(false);
  const [isModelIdWarningOpen, setIsModelIdWarningOpen] = useState(false);
  const [executionFailedNotice, setExecutionFailedNotice] = useState<{
    open: boolean;
    causeDescription: string;
    runName: string;
  }>({ open: false, causeDescription: '', runName: '' });
  const [currentAssets, setCurrentAssets] = useState<WorkbenchAssets>({});
  const [assetsChanged, setAssetsChanged] = useState(false);
  const [logs, setLogs] = useState<string>('');
  const [deploymentLogs, setDeploymentLogs] = useState<string>('');
  const [currentRunName, setCurrentRunName] = useState<string>('');
  const [currentRunDescription, setCurrentRunDescription] = useState<string>('');
  const [currentExecutionId, setCurrentExecutionId] = useState<number | null>(null);
  const [currentExecutionStatus, setCurrentExecutionStatus] = useState<ExecutionStatus | null>(null);

  const [initialRunStatus, setInitialRunStatus] = useState<RunStatus>(
    (workbenchData?.run?.status as RunStatus) || 'execution_ready'
  );

  const deployment = useDeploymentManager({
    runId: runId || '',
    initialRunStatus,
    onModalAutoOpen: () => {
      if (!isDeploymentModalOpen) {
        setIsDeploymentModalOpen(true);
      }
    },
  });

  const [executionMetrics, setExecutionMetrics] = useState<ExecutionResultModalMetric[]>([]);
  const shouldBlockRef = useRef(true);
  const executionFlowTokenRef = useRef(0);
  const lastExecutionClickAtRef = useRef(0);
  const executionAttemptInFlightRef = useRef(false);
  const skipResumeSyncRef = useRef(false);
  const currentExecutionIdRef = useRef<number | null>(null);
  const loadingAnimationRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const queuedPhaseAnimationRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const skipQueuedAnimationRef = useRef(false);
  const logIntroRef = useRef('');
  const [logSuffix, setLogSuffix] = useState('');

  const [computingResource, setComputingResource] = useState<string>(workbenchData?.computingResource || 'c4-basic');
  const [workbenchState, setWorkbenchState] = useState<WorkbenchState | null>(null);
  const [parsedLibraries, setParsedLibraries] = useState<Library[] | null>(null);
  const [parsedCode, setParsedCode] = useState<string | null>(null);
  const [executionFiles, setExecutionFiles] = useState<Record<string, string> | undefined>(undefined);
  const [isInitializing, setIsInitializing] = useState(true);
  const initializedRunIdRef = useRef<string | null>(null);
  const uploadedNewRunFilesRef = useRef<string | null>(null);
  const executionFailedModalRunDisplayNameRef = useRef<string>('');
  const executionFailedNoticeShownRef = useRef<string | null>(null);
  const workbenchSetupAnimationRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initialWorkbenchStateRef = useRef<WorkbenchState | null>(null);
  const workbenchStateRef = useRef<WorkbenchState | null>(null);
  const executionIntroStartedAtRef = useRef<Date>(new Date());

  const clearAnimationRef = useCallback((ref: React.MutableRefObject<ReturnType<typeof setInterval> | null>) => {
    if (ref.current) {
      clearInterval(ref.current);
      ref.current = null;
    }
  }, []);

  useEffect(() => {
    currentExecutionIdRef.current = currentExecutionId;
  }, [currentExecutionId]);

  useEffect(() => {
    workbenchStateRef.current = workbenchState;
  }, [workbenchState]);

  useEffect(() => {
    executionFailedModalRunDisplayNameRef.current = currentRunName.trim() || workbenchData?.run?.name?.trim() || '';
  }, [currentRunName, workbenchData?.run?.name]);

  useEffect(() => {
    const isModulesReady = !isModulesLoading && !!modulesData;
    const isRunDetailReady = !isRunDetailLoading;
    const isDeploymentReady = !deployment.state.isInitialLoading;
    const isWorkbenchReady = !isInitializing;

    const allReady = isModulesReady && isRunDetailReady && isDeploymentReady && isWorkbenchReady;

    if ((allReady || isModulesError) && isWorkbenchInitialLoading) {
      clearAnimationRef(workbenchSetupAnimationRef);
      if (isModulesError) {
        const msg = prefixWorkbenchLogBlock('작업 공간 설정에 실패했습니다.');
        setLogs(msg);
        logIntroRef.current = msg;
      } else {
        const runStatus = workbenchData?.run?.status;
        if (
          runStatus === 'execution_queued' ||
          runStatus === 'execution_running' ||
          currentExecutionStatus === 'execution_queued' ||
          currentExecutionStatus === 'execution_running'
        ) {
          const msg = prefixWorkbenchLogBlock('실험을 시작합니다.', new Date());
          setLogs(msg);
          logIntroRef.current = msg;
        } else {
          const msg = prefixWorkbenchLogBlock('작업 공간 설정이 완료되었습니다.');
          setLogs(msg);
          logIntroRef.current = msg;
        }
      }
      setIsWorkbenchInitialLoading(false);
    } else if (!allReady && !isModulesError && isWorkbenchInitialLoading) {
      if (!workbenchSetupAnimationRef.current) {
        let dotPhase = 0;
        const stamp = () => {
          const dots = dotPhase === 0 ? '' : '.'.repeat(dotPhase);
          return prefixWorkbenchLogBlock(`작업 공간을 설정하고 있습니다${dots}`, new Date());
        };

        setLogs(stamp());

        workbenchSetupAnimationRef.current = setInterval(() => {
          dotPhase = (dotPhase + 1) % 4;
          setLogs(stamp());
        }, 500);
      }
    }

    return () => {
      clearAnimationRef(workbenchSetupAnimationRef);
    };
  }, [
    clearAnimationRef,
    isModulesLoading,
    modulesData,
    isModulesError,
    isRunDetailLoading,
    deployment.state.isInitialLoading,
    isInitializing,
    isWorkbenchInitialLoading,
    currentExecutionStatus,
    workbenchData?.run?.status,
  ]);

  useEffect(() => {
    if (isModulesError) {
      notification.error({
        message: '모듈 로딩 실패',
        description: (
          <>
            모듈을 불러올 수 없습니다.
            <br />
            작업 공간 기능을 사용할 수 없습니다.
          </>
        ),
        placement: NOTIFICATION_CONFIG.PLACEMENT,
        duration: NOTIFICATION_CONFIG.DURATION.ERROR,
      });
    }
  }, [isModulesError]);

  const handleExecutionComplete = useCallback(
    async (status: ExecutionStatus) => {
      executionAttemptInFlightRef.current = false;
      setIsExecuting(false);

      if (status === 'execution_succeeded') {
        setIsCompleted(true);
        deployment.actions.updateRunStatus('execution_succeeded');

        if (workbenchStateRef.current) {
          initialWorkbenchStateRef.current = workbenchStateRef.current;
        }
        setIsPreparingResult(true);

        queryClient.invalidateQueries({ queryKey: ['run-management'] });
        queryClient.invalidateQueries({ queryKey: ['experiment-management'] });
        queryClient.invalidateQueries({ queryKey: ['projects-experiments'] });

        const flowTokenAtStart = executionFlowTokenRef.current;
        const numericRunId = runId ? parseInt(runId, 10) : NaN;
        const execId = currentExecutionIdRef.current;

        void (async () => {
          if (flowTokenAtStart !== executionFlowTokenRef.current) return;

          if (!Number.isFinite(numericRunId) || execId == null) {
            setIsPreparingResult(false);
            notification.error({
              key: 'workbench-run-metrics-missing-context',
              message: '실행 결과를 불러올 수 없습니다',
              description: '실행 정보를 확인할 수 없어 메트릭을 조회하지 못했습니다.',
              placement: NOTIFICATION_CONFIG.PLACEMENT,
              duration: NOTIFICATION_CONFIG.DURATION.ERROR,
            });
            return;
          }

          try {
            try {
              await runManagementApi.sync(numericRunId);
            } catch (syncErr) {
              console.warn(
                '[ExperimentWorkbenchPage] run sync 실패(예: HTTP 500) — 캐시 갱신을 건너뛰고 결과 모달만 계속 진행합니다.',
                syncErr
              );
            }
            if (flowTokenAtStart !== executionFlowTokenRef.current) return;

            queryClient.invalidateQueries({ queryKey: experimentKeys.runDetail(String(numericRunId)) });
            queryClient.invalidateQueries({ queryKey: experimentKeys.assetDetail(String(numericRunId), 'run') });

            await refetchRunDetail();
            if (flowTokenAtStart !== executionFlowTokenRef.current) return;

            const execution = await getExecution(numericRunId, execId);
            if (flowTokenAtStart !== executionFlowTokenRef.current) return;

            const sourceRunIdRaw = execution.sourceRunId ?? execution.source_run_id;
            const sourceRunId =
              sourceRunIdRaw != null && String(sourceRunIdRaw).trim() !== ''
                ? String(sourceRunIdRaw).trim()
                : String(numericRunId);

            let modalMetrics: ExecutionResultModalMetric[];
            try {
              const metricsResponse = await runManagementApi.getMetrics(sourceRunId);
              if (flowTokenAtStart !== executionFlowTokenRef.current) return;

              modalMetrics = mapRunMetricsResponseToExecutionModalMetrics(metricsResponse);
              if (modalMetrics.length === 0) {
                modalMetrics = getFallbackExecutionResultModalMetrics(numericRunId);
              }
            } catch (metricsErr) {
              if (flowTokenAtStart !== executionFlowTokenRef.current) return;

              console.warn(
                '[ExperimentWorkbenchPage] 메트릭 API 실패 또는 비어 있음 — 데모 store와 같은 형태의 목업 메트릭으로 모달 표시합니다.',
                metricsErr
              );
              modalMetrics = getFallbackExecutionResultModalMetrics(numericRunId);
            }

            setExecutionMetrics(modalMetrics);
            setIsPreparingResult(false);
            setIsResultModalOpen(true);
          } catch (error) {
            if (flowTokenAtStart !== executionFlowTokenRef.current) return;
            console.error('[ExperimentWorkbenchPage] 실행 결과 조회 실패:', error);
            setIsPreparingResult(false);
            notification.error({
              key: 'workbench-run-metrics-fetch-error',
              message: '실행 결과 메트릭을 불러오지 못했습니다',
              description:
                formatAxiosErrorForLog(error) || '메트릭 API 요청 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
              placement: NOTIFICATION_CONFIG.PLACEMENT,
              duration: NOTIFICATION_CONFIG.DURATION.ERROR,
            });
          }
        })();
      } else if (status === 'execution_failed') {
        deployment.actions.updateRunStatus('execution_failed');

        const numericRunId = runId ? parseInt(runId, 10) : NaN;
        const execId = currentExecutionIdRef.current;
        const failureNoticeKey = Number.isFinite(numericRunId) && execId != null ? `${numericRunId}:${execId}` : null;

        if (failureNoticeKey !== null && executionFailedNoticeShownRef.current === failureNoticeKey) {
          return;
        }
        if (failureNoticeKey !== null) {
          executionFailedNoticeShownRef.current = failureNoticeKey;
        }

        const runNameSnapshot = executionFailedModalRunDisplayNameRef.current.trim() || '이 런';

        void (async () => {
          let causeDescription = '';
          if (Number.isFinite(numericRunId) && execId != null) {
            try {
              const execution = await getExecution(numericRunId, execId);
              causeDescription = (execution.errorMessage ?? execution.error_message ?? '').trim();
            } catch (e) {
              console.error('[ExperimentWorkbenchPage] 실행 실패 상세 조회 실패:', e);
            }
          }
          setExecutionFailedNotice({ open: true, causeDescription, runName: runNameSnapshot });
        })();
      } else if (status === 'execution_canceled') {
        deployment.actions.updateRunStatus('execution_canceled');
      }
    },
    [queryClient, deployment.actions, runId, refetchRunDetail]
  );

  const executionLogs = useExecutionLogs(runId ? parseInt(runId, 10) : null, currentExecutionId, {
    onComplete: handleExecutionComplete,
    onStatusChange: (status) => {
      setCurrentExecutionStatus(status);
      deployment.actions.updateRunStatus(mapExecutionStatusToRunStatus(status));
    },
    onError: (error) => {
      console.error('[useExecutionLogs] 로그 스트리밍 실패:', error);
      executionAttemptInFlightRef.current = false;
      setIsExecuting(false);
    },
    enabled: !!currentExecutionId && isExecuting,
  });

  useEffect(() => {
    if (!isExecuting) {
      setCurrentExecutionStatus(null);
    }
  }, [isExecuting]);

  useEffect(() => {
    if (!isExecuting || currentExecutionStatus !== 'execution_queued') {
      clearAnimationRef(queuedPhaseAnimationRef);
      return;
    }

    if (skipQueuedAnimationRef.current) {
      return;
    }

    const baseMessage = '실행 환경을 준비하고 있습니다';
    const dotPatterns = ['.', '..', '...', '....', '.....', '......'];
    let i = 0;

    const tick = () => {
      const line = prefixWorkbenchLogBlock(`${baseMessage}${dotPatterns[i % dotPatterns.length]}`, new Date());
      i += 1;
      logIntroRef.current = line;
      setLogs(line);
    };

    tick();
    queuedPhaseAnimationRef.current = setInterval(tick, 380);

    return () => {
      clearAnimationRef(queuedPhaseAnimationRef);
    };
  }, [isExecuting, currentExecutionStatus, clearAnimationRef]);

  useEffect(() => {
    const deploymentLogsText = deployment.state.logs.join('\n');
    setDeploymentLogs(deploymentLogsText);
  }, [deployment.state.logs, deployment.state.status, deployment.state.step]);

  useEffect(() => {
    const stream = executionLogs.logs;
    if (
      queuedPhaseAnimationRef.current &&
      (executionLogs.isConnected || stream.length > 0 || currentExecutionStatus === 'execution_running')
    ) {
      clearInterval(queuedPhaseAnimationRef.current);
      queuedPhaseAnimationRef.current = null;
    }
    const shouldStopPlaceholder = !!stream.length || executionLogs.isConnected || executionLogs.isTerminated;

    if (shouldStopPlaceholder && loadingAnimationRef.current) {
      clearInterval(loadingAnimationRef.current);
      loadingAnimationRef.current = null;
    }

    if (stream.length > 0 || executionLogs.isConnected) {
      skipQueuedAnimationRef.current = false;
    }

    const shouldMerge =
      !!stream.length ||
      executionLogs.isConnected ||
      executionLogs.isTerminated ||
      logSuffix.length > 0 ||
      deploymentLogs.length > 0;

    if (!shouldMerge) return;

    const intro = logIntroRef.current;
    const executionLogContent = `${intro}${intro ? '\n\n' : ''}${stream}${logSuffix}`;
    const mergedLogs = deploymentLogs
      ? executionLogContent
        ? `${executionLogContent}\n\n${deploymentLogs}`
        : deploymentLogs
      : executionLogContent;

    setLogs(mergedLogs);
  }, [
    executionLogs.logs,
    executionLogs.isConnected,
    executionLogs.isTerminated,
    logSuffix,
    currentExecutionStatus,
    deploymentLogs,
  ]);

  useEffect(() => {
    if (!runId || !workbenchData) {
      navigate('/experiments', { replace: true });
      return;
    }

    const locState = location.state as LocationState | undefined;

    if (locState?.showNewRunNotice) {
      const runName = locState.showNewRunNotice;

      navigate(location.pathname, {
        replace: true,
        state: {
          workbenchData: locState.workbenchData,
          isNewExecution: locState.isNewExecution,
        },
      });
      setNewRunNoticeRunName(runName);
      return;
    }

    if (workbenchData.assetDetails) {
      setCurrentAssets({
        featureSet: workbenchData.assetDetails.featureSet,
        parameterSet: workbenchData.assetDetails.parameterSet,
        dataset: workbenchData.assetDetails.dataset,
      });
    }
    setAssetsChanged(false);
    setCurrentRunName(workbenchData.run.name);
    setCurrentRunDescription(workbenchData.run.description || '');
    setIsCompleted(isWorkbenchRunCompletedForDeploy(workbenchData.run.status));
    if (workbenchData.computingResource) {
      setComputingResource(workbenchData.computingResource);
    }
  }, [runId, workbenchData, navigate, location.key, location.pathname, location.state]);

  useEffect(() => {
    const snap = workbenchData?.editorSnapshot;
    if (snap) {
      setWorkbenchState(snap);
    }
  }, [runId, workbenchData?.editorSnapshot]);

  useEffect(() => {
    setParsedLibraries(null);
    setParsedCode(null);
    setIsInitializing(true);
    initializedRunIdRef.current = null;
    uploadedNewRunFilesRef.current = null;
    initialWorkbenchStateRef.current = null;
    setAssetsChanged(false);
    setIsWorkbenchInitialLoading(true);
  }, [runId]);

  useEffect(() => {
    const initializeWorkbench = async () => {
      if (!runId || !modulesData) return;

      if (initializedRunIdRef.current === runId) return;

      const numericRunId = parseInt(runId, 10);
      if (Number.isNaN(numericRunId)) return;

      try {
        setIsInitializing(true);
        const latestExecution = await getLatestExecution(numericRunId);
        const executionCode = latestExecution?.code || null;

        const filesMap: Record<string, string> = {};
        const locState = location.state as LocationState | undefined;
        const isNewExecution = locState?.isNewExecution ?? false;

        if (latestExecution?.files && Object.keys(latestExecution.files).length > 0) {
          Object.assign(filesMap, latestExecution.files);

          if (!isNewExecution) {
            const uploadedModuleNames = modulesData.uploaded?.map((m: { module_name: string }) => m.module_name) || [];
            const filesToUpload = Object.entries(latestExecution.files).filter(
              ([filename]) => !uploadedModuleNames.includes(filename.replace(/\.py$/, ''))
            );

            if (filesToUpload.length > 0) {
              for (const [filename, content] of filesToUpload) {
                try {
                  const blob = new Blob([content], { type: 'text/x-python' });
                  const file = new File([blob], filename, { type: 'text/x-python' });
                  await uploadModule('run', runId, file);
                } catch (error) {
                  console.error(`[ExperimentWorkbenchPage] Failed to upload ${filename}:`, error);
                }
              }
              await modulesRefetch();
            }
          }
        }

        const finalFilesMap = Object.keys(filesMap).length > 0 ? filesMap : {};
        setExecutionFiles(finalFilesMap);

        const installedLibrariesFromExecution = getInstalledLibrariesFromExecution(latestExecution, finalFilesMap);

        if (installedLibrariesFromExecution) {
          if (executionCode) {
            const { body } = splitExperimentTemplateCode(executionCode);
            setParsedCode(body);
          }
          setParsedLibraries(installedLibrariesFromExecution);
        } else if (workbenchData?.editorSnapshot?.installedLibraries) {
          const uploadedModuleNames = modulesData.uploaded?.map((m: { module_name: string }) => m.module_name) || [];
          const customModuleFiles = finalFilesMap;

          const correctedLibraries: Library[] = workbenchData.editorSnapshot.installedLibraries.map((lib) => {
            const filename = `${lib.name}.py`;
            const hasFile = !!customModuleFiles[filename];
            const isUploaded = uploadedModuleNames.includes(lib.name);

            if (isNewExecution && lib.type === 'custom') {
              if (hasFile) {
                return {
                  name: lib.name,
                  type: 'custom' as const,
                  content: customModuleFiles[filename] || '',
                };
              }
              if (isUploaded) {
                return {
                  name: lib.name,
                  type: 'standard' as const,
                };
              }
              return {
                name: lib.name,
                type: 'custom' as const,
                content: lib.content || '',
              };
            }

            if (hasFile) {
              const content = customModuleFiles[filename] || '';
              return {
                name: lib.name,
                type: 'custom' as const,
                content,
              };
            }

            if (isUploaded && lib.type === 'custom') {
              return {
                name: lib.name,
                type: 'standard' as const,
              };
            }

            return {
              name: lib.name,
              type: 'standard' as const,
            };
          });

          const existingLibraryNames = new Set(correctedLibraries.map((lib) => lib.name));
          const newCustomLibraries: Library[] = Object.keys(customModuleFiles)
            .filter((filename) => filename.endsWith('.py'))
            .map((filename) => filename.replace(/\.py$/, ''))
            .filter((moduleName) => !existingLibraryNames.has(moduleName))
            .map((moduleName) => ({
              name: moduleName,
              type: 'custom' as const,
              content: customModuleFiles[`${moduleName}.py`] || '',
            }));

          setParsedLibraries([...correctedLibraries, ...newCustomLibraries]);
        } else if (executionCode) {
          try {
            const parseResult = await fetchParse('run', {
              clientId: runId,
              code: executionCode,
            });

            if (parseResult.template && parseResult.template[0]) {
              const { body } = splitExperimentTemplateCode(parseResult.template[0].code);
              setParsedCode(body);
            }

            if (parseResult.autoImports && parseResult.autoImports.length > 0) {
              const getPackageNameFromModule = (moduleName: string): string => {
                for (const [packageName, imports] of Object.entries(LIBRARY_IMPORT_MAP)) {
                  if (imports.some((imp) => imp.module === moduleName || moduleName.startsWith(`${imp.module}.`))) {
                    return packageName;
                  }
                }
                return moduleName;
              };

              const libraries: Library[] = parseResult.autoImports.map((imp: AutoImport) => {
                const packageName = getPackageNameFromModule(imp.module);
                const filename = `${packageName}.py`;
                const hasFile = !!finalFilesMap[filename];

                if (hasFile) {
                  const content = finalFilesMap[filename] || '';
                  return {
                    name: packageName,
                    type: 'custom' as const,
                    content,
                  };
                }
                return {
                  name: packageName,
                  type: 'standard' as const,
                };
              });

              setParsedLibraries(libraries);
            }
          } catch (error) {
            console.error('[ExperimentWorkbenchPage] Failed to parse execution libraries:', error);
          }
        }

        setIsInitializing(false);
        initializedRunIdRef.current = runId;
      } catch (error) {
        console.error('[ExperimentWorkbenchPage] Failed to initialize workbench:', error);
        initializedRunIdRef.current = null;
        setIsInitializing(false);
      }
    };

    void initializeWorkbench();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, modulesData, workbenchData?.editorSnapshot?.installedLibraries]);

  useEffect(() => {
    if (!parsedCode) return;

    if (initialWorkbenchStateRef.current && initialWorkbenchStateRef.current.code !== parsedCode) {
      initialWorkbenchStateRef.current = null;
    }
  }, [parsedCode]);

  useEffect(() => {
    if (isInitializing || !parsedLibraries || executionFiles === undefined) return;

    setParsedLibraries((prev) => {
      if (!prev) return prev;

      const updated = prev.map((lib) => {
        const filename = `${lib.name}.py`;
        const hasFile = !!executionFiles[filename];

        if (hasFile) {
          return {
            name: lib.name,
            type: 'custom' as const,
            content: executionFiles[filename] || '',
          };
        }

        if (lib.type === 'custom' && !hasFile) {
          return {
            name: lib.name,
            type: 'standard' as const,
          };
        }

        return lib;
      });

      const existingLibraryNames = new Set(updated.map((lib) => lib.name));
      const newCustomLibraries: Library[] = Object.keys(executionFiles)
        .filter((filename) => filename.endsWith('.py'))
        .map((filename) => filename.replace(/\.py$/, ''))
        .filter((moduleName) => !existingLibraryNames.has(moduleName))
        .map((moduleName) => ({
          name: moduleName,
          type: 'custom' as const,
          content: executionFiles[`${moduleName}.py`] || '',
        }));

      return [...updated, ...newCustomLibraries];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitializing, executionFiles]);

  useEffect(() => {
    const uploadFilesToNewRun = async () => {
      if (!runId || !modulesData) return;

      const locState = location.state as LocationState;
      if (!locState?.isNewExecution) return;

      if (uploadedNewRunFilesRef.current === runId) return;

      const uploadedModuleNames = modulesData.uploaded?.map((m: { module_name: string }) => m.module_name) || [];
      const filesToUploadSet = new Set<string>();

      const uploadedFiles = workbenchData?.editorSnapshot?.uploadedFiles || [];
      for (const fileData of uploadedFiles) {
        const moduleName = fileData.name.replace(/\.py$/, '');
        if (!uploadedModuleNames.includes(moduleName)) {
          filesToUploadSet.add(fileData.name);
        }
      }

      const customLibraries =
        workbenchData?.editorSnapshot?.installedLibraries?.filter((lib) => lib.type === 'custom' && lib.content) || [];

      for (const lib of customLibraries) {
        const filename = `${lib.name}.py`;
        if (!uploadedModuleNames.includes(lib.name) && !filesToUploadSet.has(filename)) {
          filesToUploadSet.add(filename);
        }
      }

      if (filesToUploadSet.size === 0) {
        uploadedNewRunFilesRef.current = runId;
        return;
      }

      uploadedNewRunFilesRef.current = runId;

      const uploadedFilesMap: Record<string, string> = {};

      for (const filename of filesToUploadSet) {
        try {
          let content = '';
          const uploadedFile = uploadedFiles.find((f) => f.name === filename);
          if (uploadedFile) {
            content = uploadedFile.content;
          } else {
            const moduleName = filename.replace(/\.py$/, '');
            const lib = customLibraries.find((l) => l.name === moduleName);
            content = lib && lib.type === 'custom' ? lib.content || '' : '';
          }

          const blob = new Blob([content], { type: 'text/x-python' });
          const file = new File([blob], filename, { type: 'text/x-python' });

          await uploadModule('run', runId, file);
          uploadedFilesMap[filename] = content;
        } catch (error) {
          console.error(`Failed to upload ${filename} to new run:`, error);
        }
      }

      await queryClient.invalidateQueries({
        queryKey: ['langserver', 'modules', 'run', runId],
      });

      if (Object.keys(uploadedFilesMap).length > 0) {
        setExecutionFiles(uploadedFilesMap);
      }
    };

    void uploadFilesToNewRun();
  }, [
    runId,
    modulesData,
    workbenchData?.editorSnapshot?.uploadedFiles,
    workbenchData?.editorSnapshot?.installedLibraries,
    location.state,
    queryClient,
  ]);

  useEffect(() => {
    skipResumeSyncRef.current = false;
  }, [runId]);

  useEffect(() => {
    if (!runId || !workbenchRouteReady) return;

    const numericRunId = parseInt(runId, 10);
    if (Number.isNaN(numericRunId)) return;

    let cancelled = false;

    const syncLatestExecution = async () => {
      try {
        const latest = await getLatestExecution(numericRunId);
        if (cancelled || skipResumeSyncRef.current || !latest) return;

        skipQueuedAnimationRef.current = true;

        if (latest.status === 'execution_queued' || latest.status === 'execution_running') {
          setCurrentExecutionId(latest.id);
          setCurrentExecutionStatus(latest.status);
          setIsExecuting(true);
          setInitialRunStatus(mapExecutionStatusToRunStatus(latest.status) as RunStatus);
          const resumeMsg = prefixWorkbenchLogBlock('실험을 시작합니다.', new Date());
          logIntroRef.current = resumeMsg;
          setLogs(resumeMsg);
          return;
        }

        if (latest.status === 'execution_succeeded') {
          setIsCompleted(true);
          setCurrentExecutionId(latest.id);
          setCurrentExecutionStatus(latest.status);
          setInitialRunStatus('execution_succeeded');
        } else if (latest.status === 'execution_failed') {
          setCurrentExecutionId(latest.id);
          setCurrentExecutionStatus(latest.status);
          setInitialRunStatus('execution_failed');
        } else if (latest.status === 'execution_canceled') {
          setCurrentExecutionId(latest.id);
          setCurrentExecutionStatus(latest.status);
          setInitialRunStatus('execution_canceled');
        }
      } catch (e) {
        console.error('[ExperimentWorkbenchPage] 최신 런 조회 실패:', e);
      }
    };

    void syncLatestExecution();

    return () => {
      cancelled = true;
    };
  }, [runId, workbenchRouteReady]);

  useEffect(() => {
    executionFailedNoticeShownRef.current = null;
  }, [runId]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!shouldBlockRef.current) return false;
    if (currentLocation.pathname === nextLocation.pathname) return false;

    const stayOnWorkbenchRunOnly =
      /^\/experiments\/workbench\/[^/]+$/.test(currentLocation.pathname) &&
      /^\/experiments\/workbench\/[^/]+$/.test(nextLocation.pathname);

    if (stayOnWorkbenchRunOnly) {
      return false;
    }

    return true;
  });

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlockRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (loadingAnimationRef.current) {
        clearInterval(loadingAnimationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setPageLeaveConfirmOpen(true);
    }
  }, [blocker]);

  const handlePageLeaveConfirm = useCallback(() => {
    executionFlowTokenRef.current += 1;
    shouldBlockRef.current = false;
    blocker.proceed?.();
  }, [blocker]);

  const handlePageLeaveCancel = useCallback(() => {
    setPageLeaveConfirmOpen(false);
    blocker.reset?.();
  }, [blocker]);

  const handleWorkbenchStateChange = useCallback((state: WorkbenchState) => {
    if (initialWorkbenchStateRef.current === null) {
      initialWorkbenchStateRef.current = state;
    }
    setWorkbenchState(state);
  }, []);

  const handleLibraryLog = useCallback((message: string) => {
    setLogSuffix((prevSuffix) => {
      if (prevSuffix) {
        return `${prevSuffix}\n${message}`;
      }
      return `\n${message}`;
    });
  }, []);

  const hasChanges = useMemo(() => {
    if (!initialWorkbenchStateRef.current || !workbenchState) {
      return false;
    }

    const initialCode = initialWorkbenchStateRef.current.code;
    const currentCode = workbenchState.code;
    const codeChanged = initialCode !== currentCode;

    return codeChanged || assetsChanged;
  }, [workbenchState, assetsChanged]);

  const deployButtonDisabled =
    isWorkbenchInitialLoading ||
    isModulesError ||
    !isCompleted ||
    hasChanges ||
    (!deployment.state.isInProgress &&
      (deployment.state.runStatus === 'serving_active' || deployment.state.runStatus === 'serving_inactive'));

  const handleExecutionFilesUpdate = useCallback((files: Record<string, string>) => {
    setExecutionFiles(files);
  }, []);

  const handleExecutionConfirm = useCallback(async () => {
    if (!workbenchData || !runId || !workbenchState || !clientId) return;
    if (executionAttemptInFlightRef.current) return;
    executionAttemptInFlightRef.current = true;

    const myFlowToken = ++executionFlowTokenRef.current;

    skipResumeSyncRef.current = true;

    setIsExecuting(true);
    setIsCompleted(false);
    setCurrentExecutionId(null);
    setCurrentExecutionStatus(null);

    initialWorkbenchStateRef.current = workbenchState;
    setAssetsChanged(false);

    const currentRunStatus = deployment.state.runStatus;
    let targetRunId = parseInt(runId, 10);
    let buildResult: BuildResponse | undefined;

    try {
      const signatureMatch = workbenchState.code.match(
        /def write_your_model_experiment\([\s\S]*?\)\s*->\s*ExperimentLogBundle:\n/
      );

      let signature = '';
      let body = workbenchState.code;

      if (signatureMatch && signatureMatch.index !== undefined) {
        signature = workbenchState.code.substring(0, signatureMatch.index + signatureMatch[0].length);
        body = workbenchState.code.substring(signatureMatch.index + signatureMatch[0].length);
      }

      const template: TemplateSection[] = [
        { type: 'immutable', code: signature },
        { type: 'mutable', code: body },
      ];

      const autoImports = workbenchState.installedLibraries.flatMap((lib) => {
        if (lib.type === 'custom') {
          return [{ module: lib.name }];
        }
        const importConfigs = LIBRARY_IMPORT_MAP[lib.name];
        if (importConfigs) {
          return importConfigs;
        }
        return [{ module: lib.name }];
      });

      buildResult = await fetchBuild('run', {
        clientId,
        template,
        autoImports,
      });

      if (myFlowToken !== executionFlowTokenRef.current) return;
    } catch (error: unknown) {
      if (myFlowToken !== executionFlowTokenRef.current) return;
      console.error('[handleExecutionConfirm] 빌드 실패:', error);
      executionAttemptInFlightRef.current = false;
      setIsExecuting(false);

      const errorDetail = (error as { response?: { data?: { detail?: BuildValidationError } } })?.response?.data
        ?.detail;
      if (errorDetail && errorDetail.error === 'build_validation_failed' && errorDetail.issues) {
        const issuesText = errorDetail.issues
          .map((issue) => {
            const parts: string[] = [];
            if (issue.type) parts.push(`[${issue.type}]`);
            if (issue.message) parts.push(issue.message);
            if (issue.line) parts.push(`(line: ${issue.line})`);
            return parts.join(' ');
          })
          .join('\n');

        notification.error({
          key: 'workbench-build-validation-error',
          message: '코드 빌드 실패',
          description: issuesText || '코드에 문법 오류가 있습니다.',
          placement: NOTIFICATION_CONFIG.PLACEMENT,
          duration: NOTIFICATION_CONFIG.DURATION.ERROR,
        });
      } else {
        notification.error({
          key: 'workbench-build-error',
          message: '코드 빌드 실패',
          description: formatAxiosErrorForLog(error) || '코드 빌드 중 오류가 발생했습니다.',
          placement: NOTIFICATION_CONFIG.PLACEMENT,
          duration: NOTIFICATION_CONFIG.DURATION.ERROR,
        });
      }
      return;
    }

    if (currentRunStatus !== 'execution_ready') {
      const datasetSnapshotId = currentAssets.dataset?.datasetSnapshotId ?? null;
      const featureSetSnapshotId = currentAssets.featureSet?.featureSetSnapshotId ?? null;
      const parameterSetId = currentAssets.parameterSet?.id ?? null;

      try {
        const newRunResponse = await runManagementApi.create({
          experimentId: workbenchData.experiment.id,
          runName: bumpRunIterationDisplayName(workbenchData.run.name),
          runId: parseInt(runId, 10),
          datasetSnapshotId,
          featureSetSnapshotId,
          parameterSetId,
        });

        if (myFlowToken !== executionFlowTokenRef.current) return;

        targetRunId = newRunResponse.id;

        deployment.actions.reset();
        setInitialRunStatus('execution_running');
        deployment.actions.updateRunStatus('execution_running');
        setDeploymentLogs('');

        const mergedAssetDetails = mergeWorkbenchAssetsForNewRun(currentAssets, workbenchData.assetDetails);

        navigate(`/experiments/workbench/${newRunResponse.id}`, {
          replace: true,
          state: {
            workbenchData: {
              project: workbenchData.project,
              experiment: workbenchData.experiment,
              run: {
                name: newRunResponse.runName,
                description: workbenchData.run.description,
                status: 'execution_running',
              },
              selectedAssets: {
                ...workbenchData.selectedAssets,
                datasetId: pickSelectedAssetId('dataset', 'datasetId', currentAssets, workbenchData.selectedAssets),
                featureSetId: pickSelectedAssetId(
                  'featureSet',
                  'featureSetId',
                  currentAssets,
                  workbenchData.selectedAssets
                ),
                parameterSetId: pickSelectedAssetId(
                  'parameterSet',
                  'parameterSetId',
                  currentAssets,
                  workbenchData.selectedAssets
                ),
              },
              assetDetails: mergedAssetDetails,
              computingResource,
              editorSnapshot: {
                code: workbenchState.code,
                installedLibraries: workbenchState.installedLibraries,
                uploadedFiles: workbenchState.uploadedFiles,
              },
            },
            showNewRunNotice: newRunResponse.runName,
            isNewExecution: true,
          },
        });

        if (myFlowToken !== executionFlowTokenRef.current) return;
      } catch (error) {
        if (myFlowToken !== executionFlowTokenRef.current) return;
        console.error('[handleExecutionConfirm] 런 생성 실패:', error);
        executionAttemptInFlightRef.current = false;
        setIsExecuting(false);
        notification.error({
          key: 'workbench-run-create-error',
          message: '런 생성 실패',
          description: formatAxiosErrorForLog(error) || '새로운 런을 생성하는 중 오류가 발생했습니다.',
          placement: NOTIFICATION_CONFIG.PLACEMENT,
          duration: NOTIFICATION_CONFIG.DURATION.ERROR,
        });
        return;
      }
    }

    if (myFlowToken !== executionFlowTokenRef.current) return;

    if (currentRunStatus === 'execution_ready') {
      setCurrentExecutionStatus('execution_queued');
      skipQueuedAnimationRef.current = true;
    }

    try {
      const resourceOption =
        COMPUTING_RESOURCE_OPTIONS.find((opt) => opt.value === computingResource) ?? COMPUTING_RESOURCE_OPTIONS[0];
      const resourceSpec = {
        cpu: resourceOption.cpu,
        memory: resourceOption.memory,
        gpu: resourceOption.gpu,
      };

      const datasetSnapshotId = currentAssets.dataset?.datasetSnapshotId ?? null;
      const featureSetSnapshotId = currentAssets.featureSet?.featureSetSnapshotId ?? null;
      const parameterSetId = currentAssets.parameterSet?.id ?? null;

      const executePayload = {
        build: buildResult,
        resourceSpec,
        datasetSnapshotId,
        featureSetSnapshotId,
        parameterSetId,
      };

      if (myFlowToken !== executionFlowTokenRef.current) {
        return;
      }

      const executionResponse = await executeRun(targetRunId, executePayload, clientId);

      if (myFlowToken !== executionFlowTokenRef.current) return;

      clearAnimationRef(queuedPhaseAnimationRef);
      skipQueuedAnimationRef.current = true;

      const responseStatus = executionResponse.status || 'execution_running';
      setCurrentExecutionStatus(responseStatus);
      setCurrentExecutionId(executionResponse.id);
      setInitialRunStatus(mapExecutionStatusToRunStatus(responseStatus) as RunStatus);
      deployment.actions.updateRunStatus(mapExecutionStatusToRunStatus(responseStatus));

      setLogSuffix('');
      executionIntroStartedAtRef.current = new Date();
      logIntroRef.current = prefixWorkbenchLogBlock('실험을 시작합니다.', executionIntroStartedAtRef.current);
      setLogs(logIntroRef.current);

      const dots = ['.', '..', '...', '....'];
      let dotIndex = 0;
      loadingAnimationRef.current = setInterval(() => {
        dotIndex = (dotIndex + 1) % dots.length;
        const line = prefixWorkbenchLogBlock(`실험을 시작합니다${dots[dotIndex]}`, executionIntroStartedAtRef.current);
        logIntroRef.current = line;
        setLogs(line);
      }, 300);
    } catch (error) {
      if (myFlowToken !== executionFlowTokenRef.current) return;
      console.error('[handleExecutionConfirm] 런 실행 실패:', error);
      clearAnimationRef(loadingAnimationRef);
      clearAnimationRef(queuedPhaseAnimationRef);
      executionAttemptInFlightRef.current = false;
      setIsExecuting(false);
      setCurrentExecutionId(null);
      setCurrentExecutionStatus(null);
      const detail = formatAxiosErrorForLog(error);
      setLogSuffix((s) => (s.includes(detail) ? s : `${s}\n\n${detail}`));
      notification.error({
        key: 'workbench-run-execute-error',
        message: '실행 실패',
        description: detail || '빌드 또는 실행 요청 중 오류가 발생했습니다.',
        placement: NOTIFICATION_CONFIG.PLACEMENT,
        duration: NOTIFICATION_CONFIG.DURATION.ERROR,
      });
      return;
    }
  }, [
    workbenchData,
    runId,
    workbenchState,
    clientId,
    computingResource,
    currentAssets,
    deployment.state.runStatus,
    deployment.actions,
    navigate,
    clearAnimationRef,
  ]);

  const handleExecution = useCallback(() => {
    if (!workbenchData || !runId) return;
    const now = Date.now();
    if (now - lastExecutionClickAtRef.current < EXECUTION_CLICK_DEBOUNCE_MS) return;
    lastExecutionClickAtRef.current = now;
    void handleExecutionConfirm();
  }, [workbenchData, runId, handleExecutionConfirm]);

  const handleStop = useCallback(async () => {
    if (loadingAnimationRef.current) {
      clearInterval(loadingAnimationRef.current);
      loadingAnimationRef.current = null;
    }
    if (queuedPhaseAnimationRef.current) {
      clearInterval(queuedPhaseAnimationRef.current);
      queuedPhaseAnimationRef.current = null;
    }

    executionFlowTokenRef.current += 1;
    skipResumeSyncRef.current = true;
    skipQueuedAnimationRef.current = false;

    const numericRunId = runId ? parseInt(runId, 10) : NaN;
    const execId = currentExecutionId;

    if (!Number.isNaN(numericRunId) && execId != null) {
      try {
        const execution = await cancelExecution(numericRunId, execId);
        executionAttemptInFlightRef.current = false;
        setIsExecuting(false);
        setCurrentExecutionStatus('execution_canceled');
        deployment.actions.updateRunStatus(mapExecutionStatusToRunStatus(execution.status));
        setLogSuffix((s) => {
          if (s.includes(EXECUTION_CANCELED_LOG_MARKER)) return s;
          const line = prefixWorkbenchLogBlock(EXECUTION_CANCELED_LOG_MARKER, new Date());
          return s.trim() ? `${s}\n\n${line}` : line;
        });
        queryClient.invalidateQueries({ queryKey: ['run-management'] });
      } catch (error) {
        console.error('[handleStop] 실행 취소 실패:', error);
        executionAttemptInFlightRef.current = false;
        setIsExecuting(false);
        setCurrentExecutionStatus(null);
        deployment.actions.updateRunStatus('execution_ready');
        notification.error({
          key: 'workbench-run-cancel-error',
          message: '실행 취소 실패',
          description: formatAxiosErrorForLog(error) || '실행 취소 요청 중 오류가 발생했습니다.',
          placement: NOTIFICATION_CONFIG.PLACEMENT,
          duration: NOTIFICATION_CONFIG.DURATION.ERROR,
        });
      }
      return;
    }

    executionAttemptInFlightRef.current = false;
    setIsExecuting(false);
    setCurrentExecutionStatus(null);
    deployment.actions.updateRunStatus('execution_ready');
  }, [runId, currentExecutionId, deployment.actions, queryClient]);

  const handleDeployment = useCallback(() => {
    if (!workbenchData || !runId) return;

    if (deployment.state.isInProgress && deployment.state.runStatus === 'deployment_in_progress') {
      setDeploymentMode('start');
      setStopAcknowledgementOnly(false);
      setIsDeploymentModalOpen(true);
      return;
    }

    const modelId = runDetailData?.run?.model?.modelId;
    if (!modelId) {
      setIsModelIdWarningOpen(true);
      return;
    }

    setDeploymentMode('deploy');
    setStopAcknowledgementOnly(false);
    setIsDeploymentModalOpen(true);
  }, [workbenchData, runId, deployment.state.isInProgress, deployment.state.runStatus, runDetailData]);

  const handleDeploymentLogAdd = useCallback(
    (log: string) => {
      deployment.actions.addLog(log);
    },
    [deployment.actions]
  );

  const handleServingToggle = useCallback(
    async (checked: boolean) => {
      if (!deployment.state.deploymentId) {
        console.error('[handleServingToggle] No deployment_id available');
        return;
      }

      setIsServingToggleLoading(true);
      try {
        if (checked) {
          deployment.actions.setStep(-1);
          setDeploymentMode('start');
          setStartAcknowledgementOnly(false);
          setStopAcknowledgementOnly(false);
          setIsDeploymentModalOpen(true);
        } else {
          const timestamp = dayjs().format('HH:mm:ss');
          deployment.actions.addLog(`[${timestamp}] 서빙 비활성화를 요청하고 있습니다...`);

          await updateDeployment(deployment.state.deploymentId, { replicas: 0 });
          const detail = await getDeploymentStatus(deployment.state.deploymentId);
          deployment.actions.applyServingStopResult(detail);

          const completedTimestamp = dayjs().format('HH:mm:ss');
          deployment.actions.addLog(`[${completedTimestamp}] ✅ 서빙 비활성화가 완료되었습니다`);

          if (detail.status === DEPLOYMENT_STATUS.SERVING_INACTIVE) {
            setDeploymentMode('stop');
            setStopAcknowledgementOnly(true);
            setIsDeploymentModalOpen(true);
          }
        }
      } catch (error) {
        console.error(`[handleServingToggle] 서빙 ${checked ? '활성화' : '비활성화'} 실패:`, error);
        const errorTimestamp = dayjs().format('HH:mm:ss');
        deployment.actions.addLog(`[${errorTimestamp}] ❌ 서빙 ${checked ? '활성화' : '비활성화'} 요청에 실패했습니다`);
        message.error(
          formatAxiosErrorForLog(error) || `서빙 ${checked ? '활성화' : '비활성화'} 요청 중 오류가 발생했습니다.`
        );
      } finally {
        setIsServingToggleLoading(false);
      }
    },
    [deployment.actions, deployment.state.deploymentId]
  );

  const handleDeploymentComplete = useCallback(
    (deployId: string) => {
      deployment.actions.setDeploymentId(deployId);
    },
    [deployment.actions]
  );

  const handleStepChange = useCallback(
    (step: number) => {
      deployment.actions.setStep(step);
    },
    [deployment.actions]
  );

  const handleDeployingChange = useCallback(
    (deploying: boolean) => {
      deployment.actions.setInProgress(deploying);
    },
    [deployment.actions]
  );

  const handleServingStatusChange = useCallback(
    (status: DeploymentStatus) => {
      deployment.actions.updateRunStatus(status as RunStatus);
    },
    [deployment.actions]
  );

  const handleAssetChange = useCallback((assets: WorkbenchAssets) => {
    setCurrentAssets(assets);
    setAssetsChanged(true);
  }, []);

  const handleBreadcrumbClick = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  const resolvedWorkbenchAssets = useMemo(
    () => mergeWorkbenchAssetsForNewRun(currentAssets, workbenchData?.assetDetails),
    [currentAssets, workbenchData?.assetDetails]
  );

  if (!runId || !workbenchData) {
    return <LoadingState message="실험 목록으로 이동 중..." />;
  }

  const breadcrumbItems = [
    {
      title: (
        <span
          className={styles.breadcrumbItem}
          onClick={() => handleBreadcrumbClick(`/experiments/${workbenchData.project.id}`)}>
          <span className={styles.breadcrumbText}>{workbenchData.project.name}</span>
        </span>
      ),
    },
    {
      title: (
        <span
          className={styles.breadcrumbItem}
          onClick={() =>
            handleBreadcrumbClick(`/experiments/${workbenchData.project.id}/${workbenchData.experiment.id}`)
          }>
          <span className={styles.breadcrumbText}>{workbenchData.experiment.name}</span>
        </span>
      ),
    },
    {
      title: (
        <span className={`${styles.breadcrumbItem} ${styles.breadcrumbItemCurrent}`}>
          <span className={styles.breadcrumbText}>{workbenchData.run.name}</span>
        </span>
      ),
    },
  ];

  return (
    <div className={styles.workbench}>
      <ContentHeader
        className={styles.workbenchHeader}
        title={currentRunName}
        description={currentRunDescription}
        breadcrumbItems={breadcrumbItems}
        showCopyButton={false}
        titleRowExtra={!isWorkbenchInitialLoading ? <RunStatusTag status={deployment.state.runStatus} /> : undefined}
        actions={
          <div className={styles.actionsContainer}>
            <div className={styles.buttonRow}>
              <Button
                prefix={isExecuting ? <LoadingOutlined spin /> : <Icon name="play" size={16} />}
                onClick={handleExecution}
                disabled={isWorkbenchInitialLoading || isModulesError || isExecuting || !hasChanges}>
                실행
              </Button>
              <Button
                variant="outlined"
                prefix={<Icon name="stop" size={16} />}
                onClick={handleStop}
                disabled={
                  isWorkbenchInitialLoading ||
                  !isExecuting ||
                  currentExecutionId == null ||
                  currentExecutionStatus !== 'execution_running'
                }>
                중지
              </Button>
              <Button
                variant="outlined"
                prefix={deployment.state.isInProgress ? <LoadingOutlined spin /> : <Icon name="deployed" size={16} />}
                onClick={handleDeployment}
                disabled={deployButtonDisabled}>
                배포
              </Button>
              <ToggleSwitch
                checked={deployment.state.runStatus === 'serving_active'}
                onChange={handleServingToggle}
                loading={isServingToggleLoading || deployment.state.isInProgress}
                showLoadingIcon={false}
                width={120}
                disabled={
                  isWorkbenchInitialLoading ||
                  isModulesError ||
                  isExecuting ||
                  (deployment.state.runStatus !== 'serving_active' && deployment.state.runStatus !== 'serving_inactive')
                }
              />
              <Select
                value={computingResource}
                onChange={setComputingResource}
                options={COMPUTING_RESOURCE_OPTIONS}
                className={styles.tierSelect}
                popupMatchSelectWidth={false}
                disabled={isWorkbenchInitialLoading || isModulesError}
                labelRender={() => <Icon name="server" size={16} />}
                popupRender={(menu) => (
                  <div className={styles.tierSelectDropdown}>
                    <div className={styles.dropdownHeader}>
                      <span className={styles.dropdownHeaderText}>컴퓨팅 리소스</span>
                      <Tooltip
                        title={COMPUTING_RESOURCE_TOOLTIP}
                        placement="leftTop"
                        rootClassName={styles.resourceTooltip}
                        styles={{ root: { maxWidth: 720 } }}
                        getPopupContainer={() => document.body}>
                        <Icon name="tooltip" size={16} />
                      </Tooltip>
                    </div>
                    <div className={styles.dropdownDivider} />
                    {menu}
                  </div>
                )}
              />
            </div>
          </div>
        }
      />

      <Workbench
        key={`${runId}-${parsedLibraries || parsedCode ? 'parsed' : 'snapshot'}`}
        type="experiment"
        context={{
          projectId: workbenchData.project.id,
          experimentId: workbenchData.experiment.id,
          runId: runId!,
        }}
        initialCode={parsedCode ?? workbenchData.editorSnapshot?.code}
        initialLibraries={parsedLibraries ?? workbenchData.editorSnapshot?.installedLibraries}
        initialFeatureSet={resolvedWorkbenchAssets.featureSet}
        initialParameterSet={resolvedWorkbenchAssets.parameterSet}
        initialDataset={resolvedWorkbenchAssets.dataset}
        onStateChange={handleWorkbenchStateChange}
        onAssetChange={handleAssetChange}
        logs={logs}
        isExecuting={isExecuting}
        readOnly={isModulesError}
        scrollableReadOnlyCode={isModulesError}
        experimentTags={runDetailData?.experimentTags ?? undefined}
        executionFiles={executionFiles}
        onExecutionFilesUpdate={handleExecutionFilesUpdate}
        onLibraryLog={handleLibraryLog}
      />

      <ExecutionResultModal
        open={isResultModalOpen}
        onClose={() => setIsResultModalOpen(false)}
        metrics={executionMetrics}
        runName={currentRunName}
      />

      <DeploymentModal
        open={isDeploymentModalOpen}
        onClose={() => {
          setIsDeploymentModalOpen(false);
          setStopAcknowledgementOnly(false);
          setStartAcknowledgementOnly(false);
        }}
        mode={deploymentMode}
        stopAcknowledgementOnly={stopAcknowledgementOnly}
        startAcknowledgementOnly={startAcknowledgementOnly}
        runInfo={{
          runId: runId || '',
          model: runDetailData?.run?.model || { modelId: '' },
          modelName: currentRunName,
          artifact: {
            files: [],
          },
        }}
        deploymentState={{
          existingDeploymentId: deployment.state.deploymentId ?? undefined,
          currentStatus: deployment.state.status,
          currentStep: deployment.state.step,
          detail: deployment.state.detail,
          inProgress: deployment.state.isInProgress,
          loadingDetail: deployment.state.isLoadingDetail,
        }}
        onDeploymentChange={{
          onComplete: handleDeploymentComplete,
          onDeployingChange: handleDeployingChange,
          onStatusChange: handleServingStatusChange,
          onLogAdd: handleDeploymentLogAdd,
          onStepChange: handleStepChange,
        }}
      />

      {isPreparingResult && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <Spin
              size="large"
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin className={styles.spinnerIcon} />}
            />
            <div className={styles.loadingText}>결과 생성 중...</div>
          </div>
        </div>
      )}

      {isWorkbenchInitialLoading && (
        <div className={styles.loadingOverlay}>
          <LoadingState variant="overlay" message="작업 공간 환경 설정 중" />
        </div>
      )}

      <ConfirmModal
        open={newRunNoticeRunName !== null}
        icon={<InfoCircleOutlined className={styles.newRunNoticeModalIcon} />}
        message={<>새로운 &quot;{newRunNoticeRunName}&quot;으로 변경되어 실행되었습니다.</>}
        confirmText="확인"
        hideCancel
        maskClosable={false}
        keyboard={false}
        onConfirm={() => {}}
        onCancel={() => setNewRunNoticeRunName(null)}
      />

      <ConfirmModal
        open={pageLeaveConfirmOpen}
        message={
          <>
            워크벤치를 나가시겠습니까?
            <br />
            (편집한 내용이 있다면 저장되지 않습니다)
          </>
        }
        confirmText="이동"
        cancelText="취소"
        onConfirm={handlePageLeaveConfirm}
        onCancel={handlePageLeaveCancel}
      />

      <ConfirmModal
        open={isModelIdWarningOpen}
        message={
          <>
            배포할 모델이 설정되지 않았습니다.
            <br />
            model 변수에 학습한 모델을 할당 후 다시 실행해주세요.
          </>
        }
        confirmText="확인"
        hideCancel={true}
        onConfirm={() => {}}
        onCancel={() => setIsModelIdWarningOpen(false)}
      />

      <ConfirmModal
        open={executionFailedNotice.open}
        message={
          <>
            {`'${executionFailedNotice.runName || '이 런'}'의 실행이 실패했습니다.`}
            <br />
            {executionFailedNotice.causeDescription || EXECUTION_FAILED_FALLBACK_CAUSE}
          </>
        }
        confirmText="확인"
        hideCancel
        maskClosable={false}
        keyboard={false}
        onConfirm={() => {}}
        onCancel={() => setExecutionFailedNotice({ open: false, causeDescription: '', runName: '' })}
      />
    </div>
  );
};
